import { daysBetween } from "@shared/types";
import { lang, locale, t, tn } from "@/i18n";

/** Короткие месяцы: литовский Intl даёт «04-06», поэтому свои */
const MONTHS_SHORT: Record<string, string[]> = {
  lt: ["saus.", "vas.", "kov.", "bal.", "geg.", "birž.", "liep.", "rugp.", "rugs.", "spal.", "lapkr.", "gruod."],
  ru: ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"],  // i18n-ignore
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};
/** Месяц в родительном падеже («6 апреля», «balandžio 6 d.») */
const MONTHS_GEN: Record<string, string[]> = {
  lt: ["sausio", "vasario", "kovo", "balandžio", "gegužės", "birželio", "liepos", "rugpjūčio", "rugsėjo", "spalio", "lapkričio", "gruodžio"],
  ru: ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"],  // i18n-ignore
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};
const MS = MONTHS_SHORT[lang];
const MG = MONTHS_GEN[lang];

const parts = (d: string) => d.split("-").map(Number) as [number, number, number];
const wdFmt = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });

/** «пн» / «pr» / «Mon» */
export function weekday(d: string) {
  const [y, m, day] = parts(d);
  return wdFmt.format(new Date(Date.UTC(y, m - 1, day)));
}
export function dayNum(d: string) {
  return parts(d)[2];
}
/** «6 апреля» / «balandžio 6 d.» / «6 April» */
export function fmtDay(d: string) {
  const [, m, day] = parts(d);
  if (lang === "lt") return `${MG[m - 1]} ${day} d.`;
  return `${day} ${MG[m - 1]}`;
}
/** «6 апр» / «bal. 6» / «6 Apr» */
export function fmtDayShort(d: string) {
  const [, m, day] = parts(d);
  if (lang === "lt") return `${MS[m - 1]} ${day}`;
  return `${day} ${MS[m - 1]}`;
}
export function fmtRange(a: string, b: string) {
  const [ya, ma, da] = parts(a);
  const [yb, mb, db] = parts(b);
  if (lang === "lt") {
    if (ya === yb && ma === mb) return `${ya} m. ${MG[ma - 1]} ${da}–${db} d.`;
    if (ya === yb) return `${ya} m. ${MG[ma - 1]} ${da} d. – ${MG[mb - 1]} ${db} d.`;
    return `${ya} m. ${MG[ma - 1]} ${da} d. – ${yb} m. ${MG[mb - 1]} ${db} d.`;
  }
  if (ya === yb && ma === mb) return `${da}–${db} ${MG[ma - 1]} ${ya}`;
  if (ya === yb) return `${da} ${MG[ma - 1]} – ${db} ${MG[mb - 1]} ${yb}`;
  return `${da} ${MG[ma - 1]} ${ya} – ${db} ${MG[mb - 1]} ${yb}`;
}

export function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function countdown(start: string, end: string) {
  const today = todayLocal();
  const toStart = daysBetween(today, start);
  if (toStart > 0) return { kind: "before" as const, days: toStart, text: tn(toStart, "через {n} день|через {n} дня|через {n} дней") };
  const toEnd = daysBetween(today, end);
  if (toEnd >= 0) return { kind: "during" as const, days: -toStart + 1, text: t("день {n}", { n: -toStart + 1 }) };
  return { kind: "after" as const, days: -toEnd, text: t("поездка завершена") };
}

const timeFmt = new Map<string, Intl.DateTimeFormat>();
function fmtIn(tz: string | null | undefined, opts: Intl.DateTimeFormatOptions, key: string) {
  const id = `${key}|${tz ?? ""}`;
  let f = timeFmt.get(id);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { ...opts, hourCycle: "h23", timeZone: tz ?? undefined });
    timeFmt.set(id, f);
  }
  return f;
}

/** ISO → «18:00» в часовом поясе tz */
export function timeIn(iso: string | null, tz: string | null | undefined) {
  if (!iso) return null;
  try {
    return fmtIn(tz, { hour: "2-digit", minute: "2-digit" }, "t").format(new Date(iso));
  } catch {
    return null;
  }
}
/** ISO → «7 апр, 13:30» / «bal. 7, 13:30» в поясе tz */
export function dateTimeIn(iso: string | null, tz: string | null | undefined) {
  if (!iso) return null;
  const time = timeIn(iso, tz);
  if (!time) return null;
  return `${fmtDayShort(localDateIn(iso, tz))}, ${time}`;
}
/** Дата (YYYY-MM-DD) ISO-момента в поясе tz */
export function localDateIn(iso: string, tz: string | null | undefined) {
  try {
    return new Intl.DateTimeFormat("sv-SE", { timeZone: tz ?? undefined }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

export function durationText(min: number | null | undefined) {
  if (!min) return null;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return t("{m} мин", { m });
  return m ? t("{h} ч {m} мин", { h, m }) : t("{h} ч", { h });
}

export function money(n: number, currency = "EUR") {
  const digits = currency === "VND" ? 0 : n >= 100 ? 0 : 2;
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n);
  } catch {
    return `${n.toFixed(digits)} ${currency}`;
  }
}

const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
export function relTime(iso: string) {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return t("только что");
  if (s < 3600) return rtf.format(-Math.floor(s / 60), "minute");
  if (s < 86400) return rtf.format(-Math.floor(s / 3600), "hour");
  const d = new Date(iso);
  return fmtDayShort(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
}

export const fmtSec = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};

/** datetime-local (YYYY-MM-DDTHH:mm в поясе tz) ↔ ISO со смещением */
export function isoToLocalInput(iso: string | null, tz: string | null | undefined) {
  if (!iso) return "";
  try {
    const f = new Intl.DateTimeFormat("sv-SE", { timeZone: tz ?? undefined, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
    return f.replace(" ", "T");
  } catch {
    return iso.slice(0, 16);
  }
}
export function localInputToIso(v: string, tz: string | null | undefined) {
  if (!v) return null;
  // местное «настенное» время + смещение пояса tz на этот момент
  const asUtc = new Date(`${v}:00Z`);
  const off = tzOffsetMinutes(new Date(asUtc.getTime() - tzOffsetMinutes(asUtc, tz) * 60000), tz);
  const sign = off >= 0 ? "+" : "-";
  const abs = Math.abs(off);
  return `${v}:00${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}
function tzOffsetMinutes(d: Date, tz: string | null | undefined) {
  try {
    const s = new Intl.DateTimeFormat("en-US", { timeZone: tz ?? undefined, timeZoneName: "longOffset" }).format(d);
    const m = s.match(/GMT([+-])(\d{2}):?(\d{2})?/);
    if (!m) return 0;
    const v = Number(m[2]) * 60 + Number(m[3] ?? 0);
    return m[1] === "-" ? -v : v;
  } catch {
    return 0;
  }
}
